import { Popup } from 'components/Popup'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  useImageGeneratorCancelHandler,
  useImageGeneratorSubmitHandler,
} from '../../context/ImageGeneratorContext'

import { ChevronRightRounded, Loop } from '@mui/icons-material'
import { useGenerateImageMutation } from 'services/aiApi'
import { useCopyToS3Mutation } from 'services/miscApi'
import styled, { css } from 'styled-components/macro'
import { centered, fillAbsolutely } from 'styles/layout'
import { useKey } from 'utils/useKey'

export default function ImageGenerator() {
  const isOpen = useSelector((state: any) => state.imageGenerator.isOpen)
  const [prompt, setPrompt] = useState('')
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)

  const handleSubmit = useImageGeneratorSubmitHandler()
  const handleCancel = useImageGeneratorCancelHandler()

  const [generateImage, { data: generatedImage, error: generationError }] = useGenerateImageMutation()
  const [copyToS3] = useCopyToS3Mutation()

  useEffect(() => {
    setImageUrl(generatedImage?.imageUrl)

    return () => {
      setImageUrl(null)
    }
  }, [generatedImage])

  useKey('Escape', () => {
    if (isOpen) {
      handleCancel()
    }
  })

  // shift-enter adds newline; enter submits
  useKey('Enter', (event) => {
    if (!isOpen) {
      return
    }
    if (!event.shiftKey) {
      event.preventDefault()
      if (!generating && !saving && prompt.length != 0) {
        onGenerate()
      }
    }
  })

  if (!isOpen) return null

  const onGenerate = async () => {
    setGenerating(true)
    setError(null)
    try {
      await generateImage({ prompt })
    } catch (err) {
      setError('Generation failed: ' + JSON.stringify(err))
    } finally {
      setGenerating(false)
    }
  }

  const onSubmit = async () => {
    setSaving(true)
    setError(null)
    try {
      const response = await copyToS3({ imageUrl }) // response.data.imageUrl contains the s3 image url
      if ('error' in response) {
        throw new Error(JSON.stringify(response.error))
      } else if ('data' in response) {
        await handleSubmit({ imageUrl: response.data.imageUrl, prompt })
      }
    } catch (err) {
      setError('Generation failed: ' + JSON.stringify(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Popup verticalPadding={!imageUrl}>
      <StyledImageGenerator>
        <FancyHeader>Generate an image</FancyHeader>
        <PromptArea>
          <PromptInput
            placeholder="Enter your prompt..."
            rows={3}
            autoFocus={true}
            disabled={generating}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <GenerateButton onClick={onGenerate} disabled={generating || saving || prompt.length < 1}>
            {!generating ? (
              <ChevronRightRounded />
            ) : (
              <Loop
                sx={{
                  animation: 'spin 2s linear infinite',
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(360deg)',
                    },
                    '100%': {
                      transform: 'rotate(0deg)',
                    },
                  },
                }}
              />
            )}
          </GenerateButton>
        </PromptArea>
        {imageUrl && <img style={{ height: 500 }} src={imageUrl} />}
        {error && <div style={{ color: 'red' }}>{JSON.stringify(error)}</div>}
        {imageUrl && (
          <SaveButton onClick={onSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Use image'}
          </SaveButton>
        )}
        <CancelButton onClick={handleCancel}>Cancel</CancelButton>
      </StyledImageGenerator>
    </Popup>
  )
}

const FancyHeader = styled.p`
  font-size: 24px;
  font-weight: 600;
  user-select: none;
  font-family: 'Avenir';
  text-decoration: none;

  background-image: linear-gradient(45deg, rgba(17, 210, 219, 0.9), rgba(13, 131, 217, 0.9));
  background-size: 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-background-clip: text;
  -moz-text-fill-color: transparent;
`

const StyledImageGenerator = styled.div`
  position: relative;
  margin: 20px;
  padding: 20px;
  min-width: 500px;
  max-width: 800px;
  box-sizing: border-box;
  background: linear-gradient(45deg, rgba(17, 210, 219, 1), rgba(13, 131, 217, 1));
  border-radius: 8px;
  user-select: none;
  box-shadow: 0px 2px 6px 1px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  --angle: 0deg;
  animation: 5s rotate linear infinite;
  text-align: center;
  overflow: hidden;
  z-index: 1;

  &:before {
    ${fillAbsolutely}
    box-sizing: border-box;
    border-radius: 7px;
    margin: 1px;
    background: #ffffff;
    z-index: -1;
    content: '';
  }
`

const PromptArea = styled.div`
  position: relative;
  display: flex;
  margin: 20px auto 30px;
  padding-bottom: 42px;
  border: 1px solid #8ac5cb;
  border-radius: 10px;
  box-shadow: 0 2px 3px -1px #aaaaaa88;
`

const PromptInput = styled.textarea`
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  outline: none !important;
  border: none !important;
  font-size: 14px;
  padding: 10px;
  font: unset;
  resize: none;
  border-radius: 10px 10px 0 0;
  background-color: white; ;
`

const GenerateButton = styled.div<{ disabled?: boolean }>`
  ${centered}
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 40px;
  height: 30px;
  background-color: #0cc6da;
  color: white;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 2px 3px -1px #aaaaaa88;
  transition: 200ms background-color ease;
  pointer-events: all;

  &:hover {
    background-color: #0ca8b9;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: #94c5ca;
      pointer-events: none;
    `}
`

const SaveButton = styled.div<{ disabled?: boolean }>`
  max-width: 200px;
  margin: 20px auto;
  padding: 10px 20px;
  background-color: #0cc6da;
  color: white;
  border-radius: 30px;
  cursor: pointer;
  box-shadow: 0 2px 3px -1px #aaaaaa88;
  transition: 200ms background-color ease;

  &:hover {
    background-color: #0ca8b9;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: #94c5ca;
      pointer-events: none;
    `}
`

const CancelButton = styled.div`
  margin-top: 10px;
  color: #777;
  font-size: 12px;
  cursor: pointer;
`
